<template>
  <div class="-p-relative">
    <div
      ref="slider"
      class="generic-slider grid p-t-0 p-b-0"
      :class="{
        '-disable-slider': viewAsGrid,
        '-touch-scroll': nativeScroll && !viewAsGrid,
        '-sliding': isSliding,
        '-fullwidth': fullWidth,
      }"
      v-hammer:pan.horizontal="
        !noScroll && !nativeScroll && totalSlides > visibleSlides && !viewAsGrid
          ? swipe
          : () => {}
      "
      v-hammer:panend="
        !noScroll && !nativeScroll && totalSlides > visibleSlides && !viewAsGrid
          ? onPanEnd
          : () => {}
      "
    >
      <div
        ref="sliderWrapper"
        :class="[
          !viewAsGrid
            ? 'generic-slider-wrapper no-scrollbar'
            : 'grid-12 -view-as-grid',
          {
            '-animate': this.cssAnimate && this.ready,
            '-ready': this.ready,
            '-looped': looped,
          },
        ]"
        :style="wrapperStyle"
      >
        <slot />
      </div>
      <SliderControls
        v-if="controls && totalSlides > visibleSlides && !viewAsGrid"
        :slidesLength="totalSlides"
        :hideLeft="isOnLeftEdge"
        :hideRight="isOnRightEdge"
        :colorOverride="colorOverride"
        :circled="circledControls"
        :largeHitAreas="fullWidth"
        :navPlacement="fullWidth ? ($mqAliases.desktop ? '36px' : '18px') : '0'"
        @prev="prev"
        @next="next"
        @goto="(index) => gotoindex(index)"
      />
    </div>
  </div>
</template>
<script>
import gsap from 'gsap';
const SliderControls = () => import(/* webpackChunkName: "SliderControls" */ '@/Scripts/components/shared/GenericSlider/Assets/SliderControls.vue');
export default {
  name: 'GenericSlider',
  mixins: [
  ],
  components: {
    SliderControls
  },

  props: {
    visibleSlides: {
      type: Number,
      default: 3
    },
    noScroll: Boolean,
    backgroundColor: String,
    controls: Boolean,
    loadedLength: Number,
    snapDistance: {
      type: Number,
      default: 2
    },
    looped: Boolean,
    colorOverride: String,
    onScrollEvent: Boolean,
    changeScrollPos: Number,
    initSlide: {
      type: Number,
      default: -1
    },
    disableNativeScroll: Boolean,
    circledControls: Boolean,
    viewAsGrid: Boolean,
    fullWidth: Boolean,
    startSlide: {
      type: Number,
      default: -1
    },
    handleVideo: {
      type: Boolean,
      default: true
    },
    forceSingleStep: Boolean
  },
  data () {
    return {
      sliderPos: 0,
      lastSliderPos: 0,
      activeSlide: 1,
      cssAnimate: false,
      stopTimeout: null,
      transitionDuration: 800,
      transitionNavDuration: 800,
      transitionMaxSwipeDuration: 1500,
      speed: 100,
      isSliding: false,
      copy: 0,
      originalSlides: [],
      totalSlides: 0,
      ready: false,
      mounted: false
    };
  },
  watch: {
    viewAsGrid (newval) {
      if (newval) {
        // this.animateToGrid(this.$refs.slider.offsetHeight);
        this.sliderPos = 0;
        this.updateSlider();
      } else {
        this.$refs.slider.style.height = 'auto';
      }
    },
    isSliding (newval) {
      if (newval) {
        this.pauseSlots();
      }
    },
    changeScrollPos (newval) {
      if (newval && this.nativeScroll) {
        this.$refs.sliderWrapper.scrollLeft = newval;
      }
    },
    mounted (val) {
      if (val && this.initSlide > 0) {
        this.$nextTick(() => {
          this.gotoIndex(this.initSlide, false);
        });
      }
    }

  },
  computed: {
    nativeScroll () {
      return this.$data.Globals.isTouchDevice && !this.looped && !this.disableNativeScroll;
    },
    loopBuffer () {
      return this.originalSlides.length;
    },
    realSnapDistance () {
      return this.visibleSlides < this.snapDistance ? 1 : this.snapDistance;
    },
    useNativeScroll () {
      return this.$data.Globals.isTouchDevice;
    },

    gutter () {
      if (this.looped) {
        return this.$mqAliases.desktop ? 24 : this.$mqAliases.md ? 18 : 12;
      }
      return this.$mqAliases.desktop ? 36 : this.$mqAliases.md ? 24 : 18;
    },
    cardSpace () {
      const cardWidth = (this.sliderContentWidth / this.visibleSlides);
      return cardWidth + (this.gutter / this.visibleSlides);
    },
    sliderGutter () {
      return this.gutter - (this.gutter / this.visibleSlides);
    },
    sliderColumnSize () {
      return 100 / this.visibleSlides;
    },
    sliderViewWidth () {
      if (typeof this.$refs.slider !== 'undefined') {
        return window.innerWidth - ((window.innerWidth - this.$refs.slider.offsetWidth) / 2);
      }
      return 0;
    },
    sliderContentWidth () {
      if (typeof this.$refs.sliderWrapper !== 'undefined' && this.mounted) {
        return this.$refs.sliderWrapper.offsetWidth;
      }
      return 0;
    },
    sliderWidth () {
      const lastKey = `slide${this.totalSlides - 1}`;
      if (typeof this.$refs[lastKey] !== 'undefined') {
        const $lastEl = this.$refs[lastKey][0];
        return $lastEl.offsetLeft + $lastEl.offsetWidth;
      }
      return 0;
    },
    isOnLeftEdge () {
      return this.activeSlide === 1;
    },
    isOnRightEdge () {
      return this.activeSlide === this.endSlide;
    },
    wrapperStyle () {
      return {
        gridAutoColumns: `calc(${this.sliderColumnSize}% - ${this.sliderGutter}px)`,
        transitionDuration: `${this.transitionDuration / 1000}s`
      };
    },
    endSlide () {
      return Math.ceil(this.totalSlides - this.visibleSlides + 1);
    }
  },
  methods: {
    animateToGrid (height) {
      gsap.set(this.$refs.slider, {
        height: 'auto'
      });
      gsap.from(this.$refs.slider, 0.33, {
        css: {
          height: height + 20
        },
        ease: 'easeOut'

      });
    },
    updateSlider () {
      if (this.viewAsGrid) {
        this.$refs.sliderWrapper.style.transform = '';
      } else {
        this.$refs.sliderWrapper.style.transform = 'translateX(' + this.sliderPos + 'px)';
      }
    },
    onPanEnd (e) {
      if (this.isSliding) {
        this.swipe(e);
      }
    },
    swipe (e) {
      if (!this.isSliding) {
        this.isSliding = true;
      }
      this.sliderPos = this.lastSliderPos + e.deltaX;
      requestAnimationFrame(this.updateSlider);
      if (this.onScrollEvent) {
        this.$emit('scroll', { type: 'swipe', pos: this.sliderPos, viewWidth: this.sliderViewWidth, message: 'drag' });
      }

      const index = -Math.floor(this.sliderPos / this.cardSpace) + 1;
      if (e.isFinal) {
        const velocity = Math.min(Math.abs(e.velocityX), Math.abs(e.overallVelocityX), 3);
        this.isSliding = false;
        const dur = Math.abs(this.sliderPos - this.lastSliderPos);
        this.transitionDuration = Math.max(this.transitionMaxSwipeDuration - (dur * 3), 300);
        this.lastSliderPos = this.sliderPos;
        // Goto correct slide
        let dir = e.direction;
        if (dir !== 2 && dir !== 4) {
          dir = e.deltaX < 0 ? 2 : 4;
        }
        if (dir === 2) { // forward scroll
          const slidesToMove = Math.round((this.realSnapDistance - 1) * velocity);
          if (index + slidesToMove > this.endSlide) { // end
            this.gotoIndex(this.endSlide);
          } else {
            this.gotoIndex(index + slidesToMove);
          }
        } else if (dir === 4) { // backwards scroll
          const startSlide = 1;
          const slidesToMove = Math.round((this.realSnapDistance + 1) * velocity);
          if (index - slidesToMove <= startSlide) { // start
            this.gotoIndex(startSlide);
          } else {
            this.gotoIndex(index - slidesToMove);
          }
        }
      }
    },
    prev () {
      let gotoSlide = this.activeSlide - this.visibleSlides;
      if (this.forceSingleStep) {
        gotoSlide = this.activeSlide - 1;
      }
      if (gotoSlide < 1) {
        gotoSlide = 1;
      }
      this.gotoIndex(gotoSlide);
    },
    next () {
      let gotoSlide = this.activeSlide + this.visibleSlides;
      if (this.forceSingleStep) {
        gotoSlide = this.activeSlide + 1;
      }
      if (gotoSlide > this.endSlide) {
        gotoSlide = this.endSlide;
      }
      this.gotoIndex(gotoSlide);
    },
    gotoIndex (index, animate = true) {
      this.activeSlide = index;
      this.$emit('onChangeSlide', index);
      this.$emit('onSlideChange', index);
      if (!this.nativeScroll) {
        this.lastSliderPos = this.sliderPos = -this.cardSpace * (index - 1);
        this.updateSlider();
        if (animate) {
          this.doAnimate();
        }
      } else {
        this.lastSliderPos = this.sliderPos = (this.cardSpace - 30) * (index - 1);
        this.$refs.sliderWrapper.scrollLeft = this.sliderPos;
        // this.$refs.sliderWrapper.scrollTo({
        //   left: this.sliderPos,
        //   behavior: 'smooth'
        // });
      }
    },
    doAnimate () {
      this.cssAnimate = true;
      clearTimeout(this.stopTimeout);
      this.stopTimeout = setTimeout(() => {
        if (this.onScrollEvent) {
          this.$emit('scroll', { type: 'swipe', pos: this.sliderPos, viewWidth: this.sliderViewWidth, message: 'stop' });
        }

        this.cssAnimate = false;
        this.transitionDuration = this.transitionNavDuration;
        if (this.looped) {
          this.loopedScrollStop();
        }
      }, this.transitionDuration);
    },
    // swap back to original slide on loop end
    loopedScrollStop () {
      if (this.activeSlide <= this.loopBuffer) {
        this.gotoIndex(this.activeSlide + this.originalSlides.length, false);
      } else if (this.activeSlide > this.originalSlides.length + this.loopBuffer) {
        this.gotoIndex(this.activeSlide - this.originalSlides.length, false);
      }
    },
    cloneSlot (i) {
      const item = this.originalSlides[i];
      if (typeof item !== 'undefined') {
        const clone = Object.assign({}, item);
        clone.key = `${item.key}-copy-${this.copy}`;
        return clone;
      }
      return null;
    },
    addClone (dir, count = 0) {
      if (dir === 'before') {
        const lastSlot = this.originalSlides.length - 1;
        for (let i = lastSlot; i > lastSlot - count; i--) {
          const clone = this.cloneSlot(i);
          if (clone) {
            this.$slots.default.unshift(clone);
          }
        }
      } else if (dir === 'after') {
        for (let i = 0; i < count; i++) {
          const clone = this.cloneSlot(i);
          if (clone) {
            this.$slots.default.push(clone);
          }
        }
      }
      this.copy++;
      this.totalSlides = this.$slots.default.filter(obj => !obj.isComment).length;
    },
    pauseSlots () {
      if (this.handleVideo && this.$slots.default[this.activeSlide - 1]) {
        const video = this.$slots.default[this.activeSlide - 1].elm.getElementsByTagName('video')[0];
        if (video) {
          video.pause();
          // update copies to match currenttime of video
          const beforeVideo = this.$slots.default[this.activeSlide - this.originalSlides.length - 1].elm.getElementsByTagName('video')[0];
          const afterVideo = this.$slots.default[this.activeSlide + this.originalSlides.length - 1].elm.getElementsByTagName('video')[0];
          if (beforeVideo) {
            beforeVideo.currentTime = video.currentTime;
          }
          if (afterVideo) {
            afterVideo.currentTime = video.currentTime;
          }
        }
      }
    }
  },
  mounted () {
    if (typeof this.$slots.default !== 'undefined' && this.looped) {
      // add clones before and after original slides, and goto first original slide
      this.$nextTick(() => {
        this.addClone('before', this.loopBuffer);
        this.addClone('after', this.loopBuffer);
        this.gotoIndex(this.loopBuffer + 1, false);
        this.ready = true;
        this.$emit('ready');
      });
    } else {
      this.ready = true;
      this.$emit('ready');
    }
    this.$nextTick(() => {
      if (this.startSlide > 0 && this.startSlide > this.visibleSlides) {
        this.gotoIndex(this.startSlide - this.visibleSlides + 1);
      }
      if (this.onScrollEvent && typeof this.$refs.sliderWrapper !== 'undefined' && this.nativeScroll) {
        this.$emit('scroll', { type: 'scroll', pos: this.$refs.sliderWrapper.scrollLeft, viewWidth: this.sliderViewWidth });
        this.$refs.sliderWrapper.addEventListener('scroll', () => {
          const pos = this.$refs.sliderWrapper.scrollLeft / (this.cardSpace - 30); // hmmm why does it work with -30
          const i = Math.round(pos) + 1;
          if (Math.ceil(pos + 1) >= this.totalSlides) {
            this.activeSlide = this.totalSlides;
            this.$emit('onSlideChange', this.activeSlide);
          } else if (this.activeSlide !== i) {
            this.activeSlide = i;
            this.$emit('onSlideChange', this.activeSlide);
          }
          this.$emit('scroll', { type: 'scroll', pos: this.$refs.sliderWrapper.scrollLeft, viewWidth: this.sliderViewWidth });
        });
      }
      const loader = setInterval(() => {
        if (typeof this.$refs.sliderWrapper !== 'undefined' && this.$refs.sliderWrapper.offsetWidth > 0) {
          clearInterval(loader);
          this.mounted = true;
        }
      }, 100);
    });
  },
  created () {
    if (typeof this.$slots.default !== 'undefined') {
      this.totalSlides = this.$slots.default.filter(obj => !obj.isComment).length;
      if (this.looped) {
      // store the original list of slides
        for (let i = 0; i < this.$slots.default.length; i++) {
          const item = this.$slots.default[i];
          const clone = Object.assign({}, item);
          clone.key = `${item.key}-original`;
          this.originalSlides.push(clone);
        }
      }
    }
  }

};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";
.generic-slider {
  position: relative;
  touch-action: pan-y !important;
  &.-fullwidth {
    position: static;
  }
  &.-disable-slider {
    overflow: hidden;
    touch-action: auto !important;
  }

  &-wrapper.-animate,
  &.-sliding {
    .generic-slider-item,
    a,
    video,
    .video-wrapper {
      pointer-events: none !important;
    }
  }

  &-wrapper {
    display: grid;
    transition-timing-function: cubic-bezier(0, 0, 0.36, 1);
    grid-auto-flow: column;
    grid-gap: 18px;

    opacity: 0;
    transition: none;
    @include media-breakpoint-up(md) {
      grid-gap: 24px;
    }
    @include media-breakpoint-up(lg) {
      grid-gap: 36px;
    }
    &.-looped {
      @include media-breakpoint-down(sm) {
        grid-gap: 12px;
      }
      @include media-breakpoint-up(md) {
        grid-gap: 18px;
      }
      @include media-breakpoint-up(lg) {
        grid-gap: 24px;
      }
    }
    &.-animate {
      transition: all;
    }
    &.-ready {
      opacity: 1;
    }
  }
  .-view-as-grid {
    transition: none;
    @include media-breakpoint-down(sm) {
      grid-gap: 12px;
      .generic-slider-item {
        grid-column-end: span 6;
        &.-span12,
        &.-span8 {
            grid-column-end: span 12;
        }
      }
    }
    @include media-breakpoint-up(md) {
      grid-gap: 24px;
      .generic-slider-item {
        grid-column-end: span 6;
        &.-span12,
        &.-span8 {
            grid-column-end: span 12;
        }
      }
    }
    @include media-breakpoint-up(lg) {
      grid-gap: 36px;
      .generic-slider-item {
        grid-column-end: span 4;
        &.-span8 {
            grid-column-end: span 8;
        }
        &.-span12 {
            grid-column-end: span 12;
        }
      }
    }
    padding-left: 0;
    padding-right: 0;
  }

  a,
  img,
  video {
    user-select: none !important;
    -moz-user-select: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
  &.-touch-scroll {
    padding: 0;
    .generic-slider-wrapper {
      overflow-x: scroll;
      overflow-y: hidden;
      padding-right: 18px;
      padding-left: 18px;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
    }
    .generic-slider-item {
      position: relative;
      scroll-snap-stop: normal;
      scroll-snap-align: start;
      scroll-margin-left: 18px;
      scroll-snap-margin-left: 18px;
      scroll-margin-right: 18px;
      scroll-snap-margin-right: 18px;
      &:last-of-type {
        scroll-snap-align: end;
        &::after {
          content: "";
          display: block;
          position: absolute;
          right: -18px;
          width: 18px;
          height: 1px;
        }
      }
    }
  }
}
</style>
